import React, { FC, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { Alert, Col, Row, message } from 'antd';
import useListActivityAvailabilityQuery from 'apollo/resolvers/activityAvailability/list';
import useSetActivityAvailabilityMutation from 'apollo/resolvers/activityAvailability/set';
import { ActivityDayAvailability } from 'apollo/resolvers/activityAvailability/types';
import * as texts from 'assets/texts/canopyConfiguration';
import SeasonForm from '../SeasonForm';
import AvailabilityCalendar from '../AvailabilityCalendar';
import { API_DATE_FORMAT, AvailabilityChange, DaysAvailability } from '../types';
import styles from './AvailabilitySection.module.scss';

interface AvailabilitySectionProps {
  activityId: string;
}

const toDaysAvailability = (entries: ActivityDayAvailability[]): DaysAvailability =>
  entries.reduce<DaysAvailability>(
    (days, { day, isOpen }) => ({ ...days, [day]: isOpen }),
    {}
  );

const AvailabilitySection: FC<AvailabilitySectionProps> = ({ activityId }) => {
  const [month, setMonth] = useState<Moment>(moment());
  const [days, setDays] = useState<DaysAvailability>({});

  const { data, loading } = useListActivityAvailabilityQuery(
    activityId,
    month.clone().startOf('month').format(API_DATE_FORMAT),
    month.clone().endOf('month').format(API_DATE_FORMAT)
  );
  const [setActivityAvailabilityMutation, { loading: saving }] =
    useSetActivityAvailabilityMutation();

  useEffect(() => {
    if (data?.listActivityAvailability) {
      setDays((current) => ({
        ...current,
        ...toDaysAvailability(data.listActivityAvailability),
      }));
    }
  }, [data]);

  const applyChange = async (change: AvailabilityChange): Promise<boolean> => {
    try {
      const result = await setActivityAvailabilityMutation({
        variables: { input: { activityId, ...change } },
      });
      setDays((current) => ({
        ...current,
        ...toDaysAvailability(result.data?.setActivityAvailability || []),
      }));
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      message.success(texts.availabilityUpdated);
      return true;
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      message.error(texts.availabilityError);
      return false;
    }
  };

  return (
    <>
      <Row>
        <Col>
          <h2 className={styles.Title}>{texts.availabilityTitle}</h2>
        </Col>
      </Row>
      <Row className={styles.Row}>
        <Col span={24}>
          <Alert
            type="info"
            showIcon
            message={
              <>
                <p>{texts.hintOpenDays}</p>
                <p>{texts.hintSoldTickets}</p>
              </>
            }
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <SeasonForm onApply={applyChange} />
        </Col>
        <Col xs={24} lg={16}>
          <AvailabilityCalendar
            days={days}
            month={month}
            loading={loading || saving}
            onMonthChange={setMonth}
            onApply={applyChange}
          />
        </Col>
      </Row>
    </>
  );
};

export default AvailabilitySection;
